<script lang="ts">
  import SingleBubbleIcon from '$icons/messages/SingleBubbleIcon.svelte';
  import type { ProjectComment } from '$shared/lib';
  import Button from '../../Button.svelte';
  import { client } from '$lib/api/Client';
  import { createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { projectCommentsQueryKey } from '$lib/pages/Projects/HackathonProjectComments.svelte';
  import { toastCustom } from '$utils/toasts';
  import { ClientResponseError } from '$lib/api/ClientResponseError';
  import type { TiptapContent } from '$shared/util';
  import RichTextArea from '../../RichTextArea.svelte';
  import { browser } from '$app/environment';
  import type { Editor } from '@tiptap/core';

  export let comment: ProjectComment;
  export let showEditComment = false;
  let body: TiptapContent = comment.body;
  let editor: Editor;

  const queryClient = useQueryClient();
  const editCommentMutation = createMutation({
    mutationFn: async () => {
      return client.projects.editComment({
        commentId: comment.id,
        body: body,
      });
    },
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: projectCommentsQueryKey(comment.projectId),
      });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<{
        pages: { comments: ProjectComment[] }[];
      }>(projectCommentsQueryKey(comment.projectId));

      // Optimistically update the comment
      if (previousData) {
        const updateCommentInTree = (comments: ProjectComment[]): ProjectComment[] => {
          return comments.map((c) => {
            if (c.id === comment.id) {
              return {
                ...c,
                body: body,
                updatedAt: new Date().toISOString(),
              };
            }
            if (c.replies?.length) {
              return { ...c, replies: updateCommentInTree(c.replies) };
            }
            return c;
          });
        };

        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), {
          ...previousData,
          pages: previousData.pages.map((page) => ({
            ...page,
            comments: updateCommentInTree(page.comments),
          })),
        });
      }

      return { previousData };
    },
    onError: (error, _, context) => {
      // Roll back optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(projectCommentsQueryKey(comment.projectId), context.previousData);
      }

      if (error instanceof ClientResponseError) {
        toastCustom({
          title: 'Error',
          description: error.message,
          negative: true,
          position: 'bottom-center',
        });
      } else {
        toastCustom({
          title: 'Error',
          description: 'An unknown error occurred while editing the comment.',
          negative: true,
          position: 'bottom-center',
        });
      }
    },
  });

  async function editComment() {
    await $editCommentMutation.mutateAsync();
    showEditComment = false;
  }
</script>

<div class="bg-gray-dark-1 flex flex-col gap-y-4 rounded-md p-4">
  <div class="flex items-center gap-x-2">
    <SingleBubbleIcon class="text-gray-dark-11" />
    <h2 class="text-base">Edit Comment</h2>
  </div>
  {#if browser}
    <RichTextArea allowCodeBlock={false} bind:value={body} bind:editor />
  {/if}
  <div class="flex items-center gap-x-4">
    <Button
      tertiary
      on:click={() => (showEditComment = false)}
      disabled={$editCommentMutation.isPending}
    >
      Cancel
    </Button>
    <Button
      secondary
      disabled={!body || $editCommentMutation.isPending}
      on:click={editComment}
      loading={$editCommentMutation.isPending}
    >
      Save changes
    </Button>
  </div>
</div>
